import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronRight, Phone, AlertCircle } from "lucide-react";

function lightColor(light) {
  if (light === "red") return "#DC2626";
  if (light === "yellow") return "#F59E0B";
  return "#16A34A"; // green
}

function daysLabel(n, light) {
  if (light === "red") return "Expired";
  if (n === 1) return "1 day left";
  return `${n} days left`;
}

export default function DietitianHome() {
  const [clients, setClients] = useState([]);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    api.get("/app/dietitian/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/app/dietitian/notifications").then((r) => setAlerts(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <div className="section-eyebrow">Your clients</div>
      <h1 className="mt-1 font-display text-3xl leading-tight" style={{ color: "var(--primary)" }}>Active clients</h1>
      <p className="mt-1 text-sm" style={{ color: "var(--text-secondary)" }}>{clients.length} client{clients.length === 1 ? "" : "s"} assigned</p>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="mt-5" data-testid="alerts-banner">
          <div className="section-eyebrow flex items-center gap-2">
            <AlertCircle size={12} style={{ color: "#DC2626" }} /> Attention needed
          </div>
          <div className="mt-2 space-y-2">
            {alerts.map((a) => (
              <Link
                key={a.client_id}
                to={`/app/client/${a.client_id}`}
                data-testid={`alert-${a.client_id}`}
                className="flex items-center gap-2 p-3 rounded-xl border-2"
                style={{
                  background: a.severity === "red" ? "#FEE2E2" : "#FEF3C7",
                  borderColor: a.severity === "red" ? "#FCA5A5" : "#FDE68A",
                }}
              >
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: lightColor(a.severity) }} />
                <span className="text-xs font-bold flex-1" style={{ color: "var(--primary)" }}>{a.message}</span>
                <ChevronRight size={14} style={{ color: "var(--text-secondary)" }} />
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 space-y-2">
        {clients.length === 0 && (
          <div className="card-soft p-6 text-center text-sm" style={{ color: "var(--text-secondary)" }}>
            No clients assigned yet. The owner will assign clients to you shortly.
          </div>
        )}
        {clients.map((c) => (
          <Link key={c.id} to={`/app/client/${c.id}`} data-testid={`d-client-${c.id}`} className="card-soft p-4 flex items-center gap-3">
            <div className="relative">
              <div className="w-11 h-11 rounded-full flex items-center justify-center font-bold" style={{ background: "var(--yellow)", color: "var(--primary)" }}>
                {(c.name || "?").slice(0, 2).toUpperCase()}
              </div>
              <span
                data-testid={`traffic-${c.id}`}
                title={daysLabel(c.days_remaining ?? 0, c.status_light)}
                className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2"
                style={{ background: lightColor(c.status_light), borderColor: "var(--bg)" }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate" style={{ color: "var(--primary)" }}>{c.name}</div>
              <div className="text-xs flex items-center gap-1" style={{ color: "var(--text-secondary)" }}>
                <Phone size={10} /> {c.phone}
                {c.city && <> · {c.city}</>}
              </div>
              <div className="mt-0.5 text-[11px] font-bold" style={{ color: lightColor(c.status_light) }}>
                {daysLabel(c.days_remaining ?? 0, c.status_light)}
              </div>
            </div>
            <ChevronRight size={16} style={{ color: "var(--text-secondary)" }} />
          </Link>
        ))}
      </div>
    </div>
  );
}
